import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LANDING } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE = "7852881434";
const EMAIL = "alexkoehn49@gmail.com";

export const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", vehicle: "", location: "", preferred_date: "", preferred_time: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 7) e.phone = "Please enter a valid phone number";
    if (!form.vehicle.trim()) e.vehicle = "Tell us your vehicle (year/make/model)";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        vehicle: form.vehicle.trim(),
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.location.trim()) payload.location = form.location.trim();
      if (form.preferred_date.trim()) payload.preferred_date = form.preferred_date.trim();
      if (form.preferred_time.trim()) payload.preferred_time = form.preferred_time.trim();
      if (form.message.trim()) payload.message = form.message.trim();
      const res = await axios.post(`${API}/contact`, payload);
      if (res.data?.ok) {
        setDone(true);
        toast.success("Quote request sent! Alex will reach out shortly.");
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      toast.error("Something went wrong. Please call us at 785-288-1434.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        data-testid={LANDING.quoteSuccess}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-[#3B9BE8]/30 bg-[#0B1224] p-8 text-center"
      >
        <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#3B9BE8]/15">
          <CheckCircle2 className="h-8 w-8 text-[#3B9BE8]" />
        </div>
        <h3 className="font-display text-2xl text-white tracking-wide">Request Received!</h3>
        <p className="mt-2 text-white/65 text-sm">
          Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}! Alex will get back to you shortly with a quote. Need it faster?
        </p>
        <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
          <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-[#3B9BE8] text-[#070B14] font-bold">
            <Phone className="h-4 w-4" /> Call 785-288-1434
          </a>
          <a href={`mailto:${EMAIL}`} className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl border border-white/15 text-white hover:bg-white/5">
            <Mail className="h-4 w-4" /> Email Us
          </a>
        </div>
      </motion.div>
    );
  }

  const fieldErr = "text-xs text-red-400 mt-1";
  const inputCls = "bg-[#0B1224] border-white/12 text-white placeholder:text-white/35 h-11 focus-visible:ring-[#3B9BE8]";

  return (
    <form data-testid={LANDING.quoteForm} onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#0B1224] p-5 sm:p-7">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name" className="text-white/80 text-sm">Name *</Label>
          <Input id="name" data-testid={LANDING.quoteName} value={form.name} onChange={set("name")} placeholder="Your name" className={`mt-1.5 ${inputCls}`} />
          {errors.name && <p className={fieldErr}>{errors.name}</p>}
        </div>
        <div>
          <Label htmlFor="phone" className="text-white/80 text-sm">Phone *</Label>
          <Input id="phone" data-testid={LANDING.quotePhone} value={form.phone} onChange={set("phone")} placeholder="(785) 555-0123" inputMode="tel" className={`mt-1.5 ${inputCls}`} />
          {errors.phone && <p className={fieldErr}>{errors.phone}</p>}
        </div>
        <div>
          <Label htmlFor="email" className="text-white/80 text-sm">Email <span className="text-white/40">(optional)</span></Label>
          <Input id="email" data-testid={LANDING.quoteEmail} value={form.email} onChange={set("email")} placeholder="you@email.com" inputMode="email" className={`mt-1.5 ${inputCls}`} />
          {errors.email && <p className={fieldErr}>{errors.email}</p>}
        </div>
        <div>
          <Label htmlFor="vehicle" className="text-white/80 text-sm">Vehicle *</Label>
          <Input id="vehicle" data-testid={LANDING.quoteVehicle} value={form.vehicle} onChange={set("vehicle")} placeholder="e.g. 2014 Chevy Silverado" className={`mt-1.5 ${inputCls}`} />
          {errors.vehicle && <p className={fieldErr}>{errors.vehicle}</p>}
        </div>
      </div>

      <div className="mt-4">
        <Label htmlFor="location" className="text-white/80 text-sm">Service Location <span className="text-white/40">(optional)</span></Label>
        <Input id="location" data-testid={LANDING.quoteLocation} value={form.location} onChange={set("location")} placeholder="Address, city, or where we should meet you" className={`mt-1.5 ${inputCls}`} />
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="preferred_date" className="text-white/80 text-sm">Preferred Date <span className="text-white/40">(optional)</span></Label>
          <Input id="preferred_date" type="date" data-testid={LANDING.quoteDate} value={form.preferred_date} onChange={set("preferred_date")} className={`mt-1.5 ${inputCls} [color-scheme:dark]`} />
        </div>
        <div>
          <Label htmlFor="preferred_time" className="text-white/80 text-sm">Preferred Time <span className="text-white/40">(optional)</span></Label>
          <Input id="preferred_time" type="time" data-testid={LANDING.quoteTime} value={form.preferred_time} onChange={set("preferred_time")} className={`mt-1.5 ${inputCls} [color-scheme:dark]`} />
        </div>
      </div>

      <div className="mt-4">
        <Label htmlFor="message" className="text-white/80 text-sm">Message <span className="text-white/40">(optional)</span></Label>
        <Textarea id="message" data-testid={LANDING.quoteMessage} value={form.message} onChange={set("message")} placeholder="Tell us about your headlights and any other details." rows={4} className={`mt-1.5 bg-[#0B1224] border-white/12 text-white placeholder:text-white/35 focus-visible:ring-[#3B9BE8]`} />
      </div>
      <Button
        type="submit"
        data-testid={LANDING.quoteSubmit}
        disabled={loading}
        className="mt-5 w-full h-12 rounded-xl bg-[#3B9BE8] text-[#070B14] font-bold text-base hover:bg-[#2F8FE0] disabled:opacity-70"
      >
        {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>) : "Get My Free Quote"}
      </Button>
      <p className="mt-3 text-center text-xs text-white/45">
        No obligation. We typically reply the same day. Or call <a href={`tel:${PHONE}`} className="text-[#3B9BE8] underline">785-288-1434</a>.
      </p>
    </form>
  );
};
